import * as React from 'react';

import { useDebounce } from '@uidotdev/usehooks';
import difference from 'lodash/difference';

export const useMultiSelectEntries = <T,>({
  entries,
  selectedEntries,
  selectedKeys,
  getEntryKey,
  onSelectionChange,
  onClose,
  onReset,
  isShowingFilteredEntries,
}: {
  entries: T[] | undefined;
  selectedEntries: T[] | undefined;
  isFetching?: boolean;
  selectedKeys: Set<React.Key>;
  getEntryKey: (entry: T) => React.Key;
  onSelectionChange: (keys: Set<React.Key>) => void;
  onClose?: () => void;
  onReset: () => void;
  isShowingFilteredEntries?: boolean;
}) => {
  const onResetRef = React.useRef<() => void>(onReset);
  onResetRef.current = onReset;

  const [recentSelection, setRecentSelections] = React.useState<React.Key[]>(
    [],
  );

  const filteredEntries = React.useMemo(() => {
    const searchTermFilteredEntries = entries ?? [];
    const effectiveSelectedEntries = (selectedEntries ?? []).filter((it) =>
      selectedKeys.has(getEntryKey(it)),
    );

    const topEntries = isShowingFilteredEntries
      ? []
      : effectiveSelectedEntries.filter(
          // If a selected customer is not in the recent selection, show it at the top
          (entry) => !recentSelection.includes(getEntryKey(entry)),
        );

    const topEntriesKeySet = new Set(topEntries.map((c) => getEntryKey(c)));

    const bottomEntries = searchTermFilteredEntries.filter(
      (entry) => !topEntriesKeySet.has(getEntryKey(entry)),
    );

    return [...topEntries, ...bottomEntries];
  }, [
    isShowingFilteredEntries,
    selectedEntries,
    entries,
    selectedKeys,
    getEntryKey,
    recentSelection,
  ]);

  const debouncedFilteredEntries = useDebounce(filteredEntries, 50);

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const newSelection = difference(
        Array.from(keys),
        Array.from(selectedKeys),
      );
      setRecentSelections((s) => [...s, ...newSelection] as string[]);
      onSelectionChange(keys);
    },
    [onSelectionChange, selectedKeys],
  );

  const handleDismiss = React.useCallback(() => {
    // Reset recent selections when dismiss button is clicked
    setRecentSelections([]);
    onClose?.();
  }, [onClose]);

  const handleReset = React.useCallback(() => {
    // Reset recent selections when reset button is clicked
    setRecentSelections([]);
    onResetRef.current();
  }, []);

  // Reset recent selections when search term gets cleared
  React.useEffect(() => {
    if (!isShowingFilteredEntries) {
      setRecentSelections([]);
    }
  }, [isShowingFilteredEntries]);

  return {
    filteredEntries: debouncedFilteredEntries,
    handleSelectionChange,
    handleDismiss,
    handleReset,
  };
};
